import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import './createJob.scss';
import { ControlListOptions, CrawlfishJob, CreateCrawlfishJob, DomainAndSource, User } from '../crawler.interface';
import Button from '../../components/button/button';
import { CreateNewJobDefaultState } from '../crawler.models';
import { _createNewJob } from '../services/crawler.services';
import * as Papa from 'papaparse';
import { getErrorMessage, normalizeUrlCase } from '../../utils/utils';
import { InfoOutline, InlineEditIcon, PlusIcon, RemoveIcon } from '../../components/icons/icons';
import { outsideClick } from '../../utils/hooks';
import MultiSelector from '../../components/multiSelector/multiSelector';
import UserContext, { UserContextInterface } from '../../contexts/user-context';
import { JobMode } from '../crawler.enums';
import ToggleButton from 'src/app/components/toggleButton/toggleButton';
import DomainsTable, { DomainsTableBackendError } from './domainsTable/domainsTable';

export interface CreateJobProps {
    addJob: (newJob: CrawlfishJob) => void;
    closeModal: () => void;
    isRecreateMode: boolean
    existingJobData: CrawlfishJob;
}
export interface BulkDataRow {
    Uri: string;
    TrafficSource: string;
}
export interface TypeAheadUserState {
    inputText: string;
    suggestedOptions: User[];
}

export interface ErrorMessage {
    type: string;
    text: string;
}

export type ControlUrls = {
    [key: string]: string
}

export default function CreateJob({ addJob, closeModal, isRecreateMode, existingJobData }: CreateJobProps) {
    const [newJob, setNewJob] = useState<CreateCrawlfishJob>(CreateNewJobDefaultState);
    const [isBtnLoading, setBtnToLoading] = useState<boolean>(false);
    const [isDomainAndTrafficSourceFileUploaded, setDomainAndTrafficSourceFileIsUploaded] = useState<boolean>(false);
    const [isKeywordsFileUploaded, setKeywordsFileIsUploaded] = useState<boolean>(false);
    const [isKeywordsListShowing, showKeywordsList] = useState<boolean>(false);
    const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([]);
    const [usersVisible, setUsersVisible] = useState<boolean>(false);
    const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);
    const [state, setState] = useState<TypeAheadUserState>({
        inputText: newJob.job_owner || '',
        suggestedOptions: []
    });
    const [controlListVal, setControlListVal] = useState<ControlListOptions>(ControlListOptions.None);
    const [controlListPaths, setControlListPaths] = useState<ControlUrls>({ 0: '' });
    const [overriddenRows, setOverriddenRows] = useState<Set<number>>(new Set());
    const [backendErrorRow, setBackendErrorRow] = useState<DomainsTableBackendError | null>(null);
    const [autoLowercasedCount, setAutoLowercasedCount] = useState<number>(0);

    const { user, crawlfishUsers } = useContext<UserContextInterface>(UserContext);

    const domainsAndTrafficSourceFileUploadRef = useRef<HTMLInputElement>(null);
    const keywordsFileUploadRef = useRef<HTMLInputElement>(null);

    const userCanChangeOwner = crawlfishUsers?.length > 0 ? true : false;

    useEffect(() => {
        init();
    }, []);

    async function init() {
        if (isRecreateMode) {
            preFillModal();
        } else {
            setState({ inputText: '', suggestedOptions: crawlfishUsers });
            setNewJob({ ...newJob, job_owner: user.username });
        }
    }

    function preFillModal(): void {
        setState({ inputText: '', suggestedOptions: crawlfishUsers });

        const updatedJob = { ...newJob };

        // Set Job Name
        updatedJob.job_name = existingJobData.name || '';

        // Set Job Owner
        updatedJob.job_owner = existingJobData.owner || user.username;

        // Set Job Keywords
        if (existingJobData.keywords) {
            if (existingJobData.keywords.body && existingJobData.keywords.body.length > 0) {
                const keywordString = existingJobData.keywords.body.join('\n');   // New Line delineated 
                updatedJob.keywords = keywordString;
            }
        }
        // Set Job Rules
        console.log('[preFillModal] existingJobData.rules.ai_typing:', existingJobData.rules?.ai_typing, typeof existingJobData.rules?.ai_typing);
        if (existingJobData.rules) {
            updatedJob.rules = {
                ...newJob.rules,
                ai_typing: existingJobData.rules.ai_typing,
                max_page_count: existingJobData.rules.max_page_count,
                max_crawl_depth: existingJobData.rules.max_crawl_depth,
                max_ai_input_tokens: existingJobData.rules.max_ai_input_tokens,
                mode: existingJobData.rules.mode,
                path_control: existingJobData.rules.path_control,
                control_paths: existingJobData.rules.control_paths,
                footer_keywords: existingJobData.rules.footer_keywords,
            }
        }
        // Set domains and traffic sources
        if (existingJobData.requested_uris.length > 1) {
            const domainsToUpload: DomainAndSource[] = [];
            existingJobData.requested_uris.map((uri, index) => {
                domainsToUpload.push({
                    domain: uri,
                    source: existingJobData.sources[index]
                });
            });
            updatedJob.domains = domainsToUpload as DomainAndSource[];
        } else {
            updatedJob.domains = [
                {
                    domain: existingJobData.requested_uris[0],
                    source: existingJobData.sources[0]
                }
            ];
        }
        // Set remaining Job Data
        const finalJob = {
            ...updatedJob,
            job_owner: user.username,
            job_name: existingJobData.name
        };
        console.log('[preFillModal] setting newJob.rules.ai_typing to:', finalJob.rules.ai_typing, typeof finalJob.rules.ai_typing);
        setNewJob(finalJob);
    }

    const setValue = (event: React.ChangeEvent<HTMLInputElement>) => {
        const key = event.target.name;
        const value = event.target.value;

        if (Object.keys(newJob.rules).includes(key)) {  // Rules
            if (key === 'ai_typing' || key === 'footer_keywords') {  // Boolean values
                setNewJob({ ...newJob, rules: { ...newJob.rules, [key]: event.target.checked } });
            } else {
                setNewJob({ ...newJob, rules: { ...newJob.rules, [key]: value } });
            }
        } else if (key === 'domain') {  // Domains ==> [ {domain: 'wiki.com', source: ... } ]
            setNewJob({ ...newJob, domains: [{ ...newJob.domains[0], domain: value }] });
        } else if (key === 'source') {  // Domains ==> [ {domain: ..., source: 'Wiki Source'} ]
            setNewJob({ ...newJob, domains: [{ ...newJob.domains[0], source: value }] });
        } else {    // Name, Owner, Keywords/Phrases
            setNewJob({ ...newJob, [key]: value });
        }
    };

    const setJobMode = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (value == JobMode.Email) {
            setNewJob({
                ...newJob,
                rules: {
                    ...newJob.rules,
                    mode: value,
                    max_crawl_depth: 3,
                    max_page_count: 500
                }
            });
        } else if (value == JobMode.Standard) {
            setNewJob({
                ...newJob,
                rules: {
                    ...newJob.rules,
                    mode: value,
                    max_crawl_depth: 10,
                    max_page_count: 1000
                }
            });
        }
    };

    const setKeywordsFromArray = (keywords: string[]) => {
        // Strip leading and trailing whitespace
        const cleanedKeywords = keywords.map((keyword: string) => keyword.trim());
        const keywordString = cleanedKeywords.join('\n');   // New Line delineated 
        setNewJob({ ...newJob, keywords: keywordString });
    };

    async function submitForm(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        if (isFormValidForSubmission()) {
            setBtnToLoading(true);
            try {
                const createdJob = await _createNewJob(newJob);
                addJob(createdJob);
                setBtnToLoading(false);
                cleanupForm();
            } catch (err) {
                setBtnToLoading(false);
                const errMessage = getErrorMessage(err);
                console.error(errMessage);
                const apiErrors: ErrorMessage[] = [{ type: 'api-error', text: errMessage }];
                if (isDomainAndTrafficSourceFileUploaded) {
                    setOverriddenRows(new Set());
                }
                const match = errMessage.match(/invalid start page:\s*(\S+)/i);
                if (match && isDomainAndTrafficSourceFileUploaded) {
                    const badUrl = match[1].toLowerCase();
                    const idx = newJob.domains.findIndex(r => r.domain.toLowerCase() === badUrl);
                    if (idx !== -1) {
                        setBackendErrorRow({ index: idx, msg: errMessage });
                        apiErrors.push({
                            type: 'api-error-hint',
                            text: 'Additional domains may fail — fix this one and resubmit.'
                        });
                    }
                }
                setErrorMessages(apiErrors);
            }
        }
    }

    const assignBulkUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        //Reset domains and traffic sources file upload errors
        const currentErrors = [...errorMessages];
        const index = currentErrors.findIndex((err: ErrorMessage) => err.type == 'bulk-upload');
        if (index !== -1) {
            currentErrors.splice(index, 1);
            setErrorMessages([...currentErrors]);
        }

        if (!e.target.files) return;
        const file = e.target.files[0];
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                _parseDomainAndTrafficSourceFile(results.data as unknown as any[]);  // eslint-disable-line @typescript-eslint/no-explicit-any
            }
        });
    };

    const assignKeywordsFromFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        // Reset keywords file upload errors
        const currentErrors = [...errorMessages];
        const index = currentErrors.findIndex((err: ErrorMessage) => err.type == 'keyword-file-err');
        if (index !== -1) {
            currentErrors.splice(index, 1);
            setErrorMessages([...currentErrors]);
        }

        if (!e.target.files) return;
        const file = e.target.files[0];
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                _parseKeywordsFile(results.data as unknown as any[]);   // eslint-disable-line @typescript-eslint/no-explicit-any
            }
        });
    };

    const _parseKeywordsFile = async (jsonData: any[]) => {             // eslint-disable-line @typescript-eslint/no-explicit-any
        setKeywordsFileIsUploaded(true);
        // Keywords are just a string of words separated by newline \n characters
        const keywordsFromFile: string[] = [];

        // Trim whitespace
        const keywordHeader = Object.keys(jsonData[0])[0];

        if (keywordHeader.trim().toLocaleLowerCase() !== 'keywords') {
            setErrorMessages([...errorMessages, {
                type: 'keyword-file-err',
                text: 'Column header for Keywords CSV must be "Keywords".'
            }]);
            return;
        } else {
            jsonData.forEach((row: any) => {    // eslint-disable-line @typescript-eslint/no-explicit-any
                keywordsFromFile.push(row[keywordHeader]);
            });
        }


        if (keywordsFromFile.length < 1) {
            setErrorMessages([...errorMessages, {
                type: 'keyword-file-err',
                text: 'Must submit at least 1 keyword in file.'
            }]);
            return;
        }

        setKeywordsFromArray(keywordsFromFile);
    };

    const _parseDomainAndTrafficSourceFile = async (jsonData: any[]) => {  // eslint-disable-line @typescript-eslint/no-explicit-any
        setDomainAndTrafficSourceFileIsUploaded(true);
        setOverriddenRows(new Set());
        setBackendErrorRow(null);
        setAutoLowercasedCount(0);
        // comma separate URI, Traffic Source for now, to be updated in the future
        const urisAndTrafficSources: DomainAndSource[] = [];

        // Trim whitespace
        const uriHeader = Object.keys(jsonData[0])[0];
        const trafficSourceHeader = Object.keys(jsonData[0])[1];

        if (uriHeader.trim() !== 'Uri' || trafficSourceHeader.trim() !== 'TrafficSource') {
            setErrorMessages([...errorMessages, {
                type: 'bulk-upload',
                text: 'Column headers must be "Uri" and "TrafficSource".'
            }]);
            return;
        }

        let lowercasedCount = 0;
        jsonData.forEach((row: any) => {    // eslint-disable-line @typescript-eslint/no-explicit-any
            const rawDomain: string = row[uriHeader] ?? '';
            const normalized = normalizeUrlCase(rawDomain);
            if (normalized !== rawDomain) lowercasedCount++;
            urisAndTrafficSources.push({ domain: normalized, source: row[trafficSourceHeader] });
        });

        if (urisAndTrafficSources.length > 1000) {
            setErrorMessages([...errorMessages, {
                type: 'bulk-upload',
                text: 'Cannot exceed 1,000 domains.'
            }]);
            return;
        }

        setAutoLowercasedCount(lowercasedCount);
        const maxPageCountDefault = 1000;
        setNewJob({ ...newJob, domains: urisAndTrafficSources, rules: { ...newJob.rules, max_page_count: maxPageCountDefault } });
    };

    const clearDomainAndTrafficSourceInput = () => {
        if (domainsAndTrafficSourceFileUploadRef.current) {
            domainsAndTrafficSourceFileUploadRef.current.value = '';
            setNewJob({ ...newJob, domains: [{ domain: '', source: '' }], rules: { ...newJob.rules, max_page_count: 1000 } });
            setDomainAndTrafficSourceFileIsUploaded(false);
            setOverriddenRows(new Set());
            setBackendErrorRow(null);
            setAutoLowercasedCount(0);
            const allErrors = [...errorMessages];
            const nonDomainAndTrafficSourceErrors = allErrors.filter((err: ErrorMessage) => err.type !== 'bulk-upload' && err.type !== 'api-error' && err.type !== 'api-error-hint');
            setErrorMessages([...nonDomainAndTrafficSourceErrors]);
        }
    };

    const clearKeywordsInput = () => {
        if (keywordsFileUploadRef.current) {
            keywordsFileUploadRef.current.value = '';
            setNewJob({ ...newJob, keywords: '' });
            setKeywordsFileIsUploaded(false);
            const allErrors = [...errorMessages];
            const nonKeywordErrors = allErrors.filter((err: ErrorMessage) => err.type !== 'keyword-file-err');
            setErrorMessages([...nonKeywordErrors]);
        }
    };

    const cleanupForm = () => {
        setNewJob(CreateNewJobDefaultState);
        setOverriddenRows(new Set());
        setBackendErrorRow(null);
        setAutoLowercasedCount(0);
        closeModal();
    };

    const updateDomainRow = useCallback((index: number, field: 'domain' | 'source', value: string) => {
        setNewJob(prev => {
            const domains = [...prev.domains];
            domains[index] = { ...domains[index], [field]: value };
            return { ...prev, domains };
        });
        setBackendErrorRow(prev => (prev?.index === index ? null : prev));
    }, []);

    const toggleDomainRowOverride = useCallback((index: number) => {
        setOverriddenRows(prev => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    }, []);

    function isFormValidForSubmission() {
        // Start with only BulkUpload errors
        const errors = errorMessages.filter((err: ErrorMessage) => err.type == 'bulk-upload' || err.type == 'keyword-file-err');
        // Check for Name and Owner
        for (const [key, value] of Object.entries(newJob)) {
            if (
                ((key == 'job_name' || key == 'job_owner') && value?.length == 0) ||                 // no name or owner
                ((key == 'domains') && (newJob[key][0].domain == '' || newJob[key][0].source == '')) // no domains or traffic sources
            ) {
                // if ErrorMessage does not exist, add it
                if (errors.findIndex((err: ErrorMessage) => err.type == 'input-fields') == -1) {
                    errors.unshift({
                        type: 'input-fields',
                        text: 'Job must have a valid Name, Owner, Traffic Source, and at least one URL.'
                    });
                }
            }
        }
        // Check Max Ai Input Tokens
        if (newJob.rules && newJob.rules.max_ai_input_tokens) {
            if (newJob.rules.max_ai_input_tokens > 10000) {
                errors.unshift({
                    type: 'input-fields',
                    text: 'Max Ai Input Tokens must not exceed 10,000.'
                });
            } else if (newJob.rules.max_ai_input_tokens < 100) {
                errors.unshift({
                    type: 'input-fields',
                    text: 'Max Ai Input Tokens must not be less than 100.'
                });
            }
        }
        setErrorMessages([...errors]);
        return errors.length > 0 ? false : true;
    }

    const outerRef = useRef<HTMLDivElement>(null);
    outsideClick(outerRef, () => setUsersVisible(false));

    function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        let suggestions: User[] = [];
        const value = e.target.value;
        setState({ ...state, inputText: value });
        setNewJob({ ...newJob, job_owner: value });
        if (value.length > 0) {
            // const regex = new RegExp(`^ ${ value }`, `i`);
            // suggestions = options.sort().filter(v => regex.test(v));
            suggestions = crawlfishUsers.sort().filter(user => user.username.includes(value));
        } else {
            suggestions = crawlfishUsers;
        }

        setState({
            inputText: value, suggestedOptions: suggestions
        });
    }

    function suggestionSelected(newUser: User) {
        setNewJob({ ...newJob, job_owner: newUser.username });
        setUsersVisible(false);
        setState({ inputText: newUser.username, suggestedOptions: state.suggestedOptions });
    }

    function renderOptions() {
        const searchOptions = state.suggestedOptions;
        if (searchOptions.length === 0) {
            return;
        }
        return (
            <ul className="type-ahead-list">
                {searchOptions.map(user => <li key={user.created_at} onClick={() => suggestionSelected(user)}>{user.username}</li>)}
            </ul>
        );
    }

    const viewUsers = () => {
        if (userCanChangeOwner) {
            setUsersVisible(true);
        }
    };

    const handlePathControlSelect = (option: ControlListOptions) => {
        setControlListVal(option);
        setNewJob({
            ...newJob,
            rules: {
                ...newJob.rules,
                path_control: option,
                control_paths: Object.values(controlListPaths),
            }
        });
    };

    const addControlListPath = () => {
        const pathsListCopy = { ...controlListPaths };
        const pathListVals: string[] = Object.values(pathsListCopy);
        const index = pathListVals.length;

        if (pathListVals[pathListVals.length - 1].length > 0) {
            setControlListPaths({ ...pathsListCopy, [index]: '' });
        }
    };

    const updateControlListPath = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const newVal = event.target.value;

        setControlListPaths((prevState) => {
            const updatedPaths: ControlUrls = { ...prevState, [index]: newVal };

            setNewJob((prevJob) => ({
                ...prevJob,
                rules: {
                    ...prevJob.rules,
                    control_paths: Object.values(updatedPaths).filter((path: string) => path.length > 0) as string[], // Cast to string[]
                }
            }));

            return updatedPaths;
        });
    };

    const deleteControlListPath = (index: number) => {
        if (index === 0) {
            setControlListPaths((prevState) => {
                const newState = { ...prevState };
                newState[index] = '';

                setNewJob((prevJob) => ({
                    ...prevJob,
                    rules: {
                        ...prevJob.rules,
                        control_paths: Object.values(newState).filter((path) => path.length > 0)
                    }
                }));

                return newState;
            });
        } else {
            setControlListPaths((prevState) => {
                const newState = { ...prevState };
                delete newState[index];

                setNewJob((prevJob) => ({
                    ...prevJob,
                    rules: {
                        ...prevJob.rules,
                        control_paths: Object.values(newState).filter((path) => path.length > 0),
                    }
                }));

                return newState;
            });
        }
    };

    function toggleIncludeFooter(): void {
        setNewJob({
            ...newJob, rules: {
                ...newJob.rules,
                'footer_keywords': !newJob.rules.footer_keywords
            }
        });
    }

    function toggleAiTyping(): void {
        setNewJob({
            ...newJob, rules: {
                ...newJob.rules,
                'ai_typing': !newJob.rules.ai_typing
            }
        });
    }

    const preDefinedKeywords = React.useMemo(() => {
        if (newJob.keywords && newJob.keywords !== '') {
            return newJob.keywords.split('\n').map((word: string) => word);
        } else if (existingJobData?.keywords && existingJobData.keywords.body && existingJobData.keywords.body[0] !== '') {
            return existingJobData.keywords.body.map((word: string) => word) || [];
        }
    }, [existingJobData, newJob.keywords]);

    return (
        <div className="content">
            <form className="new-job-form" onSubmit={(e) => submitForm(e)} role="form">
                {isRecreateMode ? <p style={{ fontSize: '1.1em', textAlign: 'center' }}>Restarting Job: <span style={{ fontWeight: 'bold' }}> {existingJobData.name} </span> </p> : null}

                <div className="grid-row" data-cy="grid-row-job-name">
                    <label htmlFor="name">Job Name:</label>
                    <input onChange={setValue} type="text" name="job_name" value={newJob.job_name} id="name" data-testid="job-name-input" />
                </div>

                <div className="grid-row" ref={outerRef} data-cy="grid-row-job-owner">
                    <label htmlFor="owner">Job Owner:</label>
                    {!usersVisible ?
                        <div className={"job-owner-field edit-with-inline " + (userCanChangeOwner ? 'editable' : 'un-editable')} onClick={viewUsers}>
                            <span className="current-job-name" title={newJob.job_owner}>{newJob.job_owner}</span>
                            {userCanChangeOwner &&
                                <span className="edit-icon">
                                    <InlineEditIcon />
                                </span>
                            }
                        </div>
                        :
                        <div className="users-dropdown">
                            <input className="job-owner-input-div"
                                type="text"
                                name="job_owner"
                                placeholder="Search by email"
                                id="owner"
                                autoComplete="off"
                                onChange={handleInputChange}
                            />
                            <div className="users-dropdown-content">
                                {renderOptions()}
                            </div>
                        </div>
                    }
                </div>

                <div className={"grid-row phrases-row " + (isKeywordsFileUploaded ? 'disabled' : '')} data-cy="grid-row-phrases-select">
                    <MultiSelector
                        key={isKeywordsFileUploaded ? 'uploaded' : 'not-uploaded'}
                        label="Keywords/Phrases"
                        setParentTags={setKeywordsFromArray}
                        clear={isBtnLoading || isKeywordsFileUploaded}
                        inputClass="create-job-input"
                        isTypeAhead={false}
                        defaultSelected={preDefinedKeywords}
                    />
                </div>

                <div className="grid-row phrases-file-row">
                    <div className="phrases-file-label">
                        <label htmlFor="phrases-file">Upload Keywords:</label>
                        <span>*CSV of Keywords</span>
                    </div>
                    <input type="file" accept=".csv" onChange={assignKeywordsFromFile} ref={keywordsFileUploadRef} name="phrases-file" placeholder="CSV of keywords" id="phrases-file" />
                </div>

                {isKeywordsFileUploaded &&
                    <div className="grid-row full-row  bulk-phrases-row">
                        <div className="bulk-phrases-content-wrapper">
                            <a className="toggle-phrases-display" onClick={() => showKeywordsList(prevState => !prevState)}>
                                <i>
                                    {isKeywordsListShowing ? 'Hide' : 'Show'} Keywords
                                </i>
                            </a>
                            <div className={"bulk-phrases-display " + (isKeywordsListShowing ? "showing" : "hidden")}>
                                {newJob.keywords.split('\n').map((phrase: string, index: number) => (
                                    <span key={phrase + index}>{phrase}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                }

                <div className="grid-row include-footer-row">
                    <label htmlFor="include-footer">Include Footer:</label>
                    <ToggleButton handleClick={toggleIncludeFooter} dataTestId='include-footer' label={
                        newJob.rules.footer_keywords ?
                            <label>Include</label>
                            :
                            <label className="italicized">Do not include</label>
                    } isOn={newJob.rules?.footer_keywords || false} />
                </div>

                <div className="grid-row mode-row">
                    <label htmlFor="mode">Mode:</label>
                    <div className="mode-radio-buttons">
                        <div className="mode-opt">
                            <input checked={newJob.rules.mode == JobMode.Standard} type="radio" name="mode" value={JobMode.Standard} onChange={setJobMode} id="standard" data-testid="standard-mode-input" />
                            <label htmlFor="standard">Standard (Default)</label>
                        </div>
                        <div className="mode-opt">
                            <input checked={newJob.rules.mode == JobMode.Email} type="radio" name="mode" value={JobMode.Email} onChange={setJobMode} id="email" data-testid="email-mode-input" />
                            <label htmlFor="email">Email</label>
                        </div>
                    </div>
                </div>

                <div className="grid-row ai-typing-row">
                    <label htmlFor="ai-typing">AI Typing:</label>
                    {console.log('[CreateJob render] ai_typing:', newJob?.rules?.ai_typing, '| isOn will be:', newJob?.rules?.ai_typing || false)}
                    <ToggleButton handleClick={toggleAiTyping} dataTestId='ai-typing' label={
                        newJob?.rules?.ai_typing ?
                            <label>On</label>
                            :
                            <label className="italicized">Off</label>
                    } isOn={newJob?.rules?.ai_typing || false} />
                </div>

                {newJob.rules.ai_typing ?
                    <div className="grid-row max-ai-input-tokens">
                        <div className="max-ai-input-tokens-label">
                            <label htmlFor="max-ai-input-tokens">Max AI Input Tokens:</label>
                            <span>*Min: 100 / Max: 10,000</span>
                        </div>
                        <input type="number" onChange={setValue} name="max_ai_input_tokens" min="100" max="10000" value={newJob.rules.max_ai_input_tokens} id="max-ai-input-tokens" data-testid="max-ai-input-tokens-input" />
                    </div>
                    : null
                }

                <div className="grid-row path-control-row">
                    <label htmlFor="path-control">Path Control List:</label>
                    <select
                        id="path-control"
                        name="path-control"
                        data-testid="path-control-select"
                        onChange={(e) => handlePathControlSelect(e.target.value as ControlListOptions)}
                        defaultValue={ControlListOptions.None}
                    >
                        <option value="none" label="none" data-testid="path-control-option"></option>
                        <option value="whitelist" label="Whitelist" data-testid="path-control-option"></option>
                        <option value="blacklist" label="Blacklist" data-testid="path-control-option"></option>
                    </select>
                </div>

                {controlListVal !== ControlListOptions.None ? (
                    <div className="grid-row paths-list-rows">
                        <label htmlFor="paths-list" className="path-list-label">Control Paths:</label>
                        {Object.entries(controlListPaths).map(([key, path]) => (
                            <div className="add-path-row" key={key}>
                                <input
                                    className="add-path-input"
                                    type="text"
                                    value={path}
                                    data-testid={"path-input-" + key}
                                    onChange={(e) => updateControlListPath(e, Number(key))}
                                />
                                <button className="add-path-btn" type="button" onClick={addControlListPath} data-testid={"add-path-btn-" + key}>
                                    <PlusIcon />
                                </button>
                                <button
                                    className="remove-path-btn"
                                    type="button"
                                    data-testid={"remove-path-btn-" + key}
                                    onClick={() => deleteControlListPath(Number(key))}
                                >
                                    <RemoveIcon />
                                </button>
                            </div>
                        ))}
                    </div>
                ) : null}

                <div className="grid-row depth-row">
                    <label htmlFor="depth">Depth:</label>
                    <input type="number" onChange={setValue} name="max_crawl_depth" min="1" max="10" value={newJob.rules.max_crawl_depth} id="depth" data-testid="depth" />
                </div>

                <div className="grid-row max-pages-row">
                    <div className="max-pages-label">
                        <label htmlFor="max-pages">Max Pages:</label>
                        <span>*Max pages per domain</span>
                    </div>
                    <input type="number" onChange={setValue} name="max_page_count" min="1" value={newJob.rules.max_page_count} id="max-pages" data-testid="max-page-count" />
                </div>

                {!isDomainAndTrafficSourceFileUploaded && (
                    <>
                        <div className="grid-row url-row">
                            <label htmlFor="urls">URL:</label>
                            <input type="text" onChange={setValue} name="domain" placeholder="" value={newJob.domains[0]?.domain} id="urls" data-testid="url-input" />
                        </div>

                        <div className="grid-row traffic-source-row">
                            <label htmlFor="traffic-source">Traffic Source:</label>
                            <input type="text" onChange={setValue} name="source" placeholder="" value={newJob.domains[0]?.source} id="traffic-source" data-testid="traffic-source-input" />
                        </div>
                    </>
                )}

                <div className="grid-row bulk-upload-row">
                    <span className="bulk-upload-label">
                        <span className={"tooltip " + (tooltipVisible ? "highlight-tooltip" : "")} onClick={() => setTooltipVisible(prevState => !prevState)}>
                            <InfoOutline />
                        </span>
                        <label htmlFor="bulk-upload">
                            Bulk Upload
                        </label>
                    </span>
                    <input type="file" accept=".csv" onChange={assignBulkUpload} ref={domainsAndTrafficSourceFileUploadRef} name="bulk-upload" placeholder="remediation, failed, passed" id="bulk-upload" />

                </div>

                <div className="grid-row csv-tooltip-row">
                    {tooltipVisible &&
                        <span className="info bulk-upload-info">*Only CSV with columns titled &quot;Uri&quot; and &quot;TrafficSource&quot; exactly are accepted.</span>
                    }
                </div>

                {isDomainAndTrafficSourceFileUploaded && (
                    <div className="grid-row full-row">
                        <DomainsTable
                            rows={newJob.domains}
                            onChangeRow={updateDomainRow}
                            overriddenRows={overriddenRows}
                            onToggleOverride={toggleDomainRowOverride}
                            backendErrorRow={backendErrorRow}
                            autoLowercasedCount={autoLowercasedCount}
                        />
                    </div>
                )}

                {isKeywordsFileUploaded ?
                    <div className="grid-row">
                        <span className="clear-file">
                            <a onClick={clearKeywordsInput} >
                                <RemoveIcon />
                                Remove Keyword File
                            </a>
                        </span>
                    </div>
                    : null
                }

                {isDomainAndTrafficSourceFileUploaded ?
                    <div className="grid-row">
                        <span className="clear-file">
                            <a onClick={clearDomainAndTrafficSourceInput} >
                                <RemoveIcon />
                                Remove Bulk Upload File
                            </a>
                        </span>
                    </div>
                    : null
                }
                <ul className="error-message-section">
                    {
                        errorMessages.map((msg: ErrorMessage) => (
                            <li key={msg.text} className="error-message"> {msg.text} </li>
                        ))
                    }
                </ul>
                <Button cyTestId="new-job-button" variant="button button--solid button--action centered" isDisabled={isBtnLoading} isLoading={isBtnLoading} type="submit">Submit</Button>
            </form>
        </div>
    );
}
